import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Table, Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Box, Text } from 'atoms';
import {
	Container,
	columnsRegularTrip,
	columnsAdhocTrip,
	columnsNoLoadTrip,
	TitleText,
	SubTitle,
	Tag,
	Card,
	Flex,
	FlexChild,
} from './VendorInvoice';
import PaymentDetailsModal from 'pages/finance/payments/components/PaymentDetailsModal';
import { API } from 'api';
import { createInvoicePayment, searchInvoicePayments } from 'store/vendorBillApprovalSlice';
import ProformaPdfViewerModal from './components/ProformaPdfViewerModal';
import InvoiceSummaryCard from './components/InvoiceSummaryCard';
import EditableTotalPayable from './components/EditableTotalPayable';
import EditHistoryTable from './components/EditHistoryTable';
import PaymentHistoryTable from './components/PaymentHistoryTable';
import { getUIVersion } from 'utils/router-static-data';

const TitleRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

const MarkPaymentButton = styled(Button)`
	background: #143e60;
	color: #ffffff;
	border: none;
	&:hover {
		background: #1a4d7a;
		color: #ffffff;
	}
`;

const getAdvancePaymentSummary = (adhocTripData) => {
	const totalAdvanceAmount = adhocTripData?.reduce(
		(sum, record) => sum + (Number(record.advance_amount) || 0),
		0
	);
	return (
		<Table.Summary.Row>
			<Table.Summary.Cell index={0}>
				<strong>Total</strong>
			</Table.Summary.Cell>
			<Table.Summary.Cell index={1} />
			<Table.Summary.Cell index={3} />
			<Table.Summary.Cell index={4} />
			<Table.Summary.Cell index={5}>
				<strong>₹ {totalAdvanceAmount}</strong>
			</Table.Summary.Cell>
		</Table.Summary.Row>
	);
};

// Build columns for Advance Details table — accepts a setter to open the PDF viewer modal
const getAdvancePaymentColumns = (onViewPdf) => [
	{
		title: 'Trip Id',
		dataIndex: 'trip_id',
		key: 'trip_id',
		render: (value) => value || '-',
	},
	{
		title: 'UTR Number',
		dataIndex: 'advance_utr_number',
		key: 'advance_utr_number',
		render: (value) => value || '-',
	},
	{
		title: 'Proforma Invoice No',
		dataIndex: 'proforma_invoice_number',
		key: 'proforma_invoice_number',
		render: (value) => value || '-',
	},
	{
		title: 'Proforma PDF',
		key: 'proforma_pdf',
		render: (_, record) =>
			record.proforma_view_url ? (
				<span
					style={{ cursor: 'pointer', color: '#d32f2f', fontSize: '18px' }}
					title="View Proforma PDF"
					onClick={() => onViewPdf(record)}
					role="button"
					tabIndex={0}
					onKeyDown={(e) => e.key === 'Enter' && onViewPdf(record)}
				>
					📄
				</span>
			) : (
				'-'
			),
	},
	{
		title: 'Advance Amount',
		dataIndex: 'advance_amount',
		key: 'advance_amount',
		render: (value) => (value !== undefined && value !== null && value !== '' ? value : '-'),
	},
];

const getAdvancePaymentData = (adhocTripData) => {
	if (!Array.isArray(adhocTripData)) return [];
	return adhocTripData.flatMap((item) =>
		Array.isArray(item.advance_payments) ? item.advance_payments : []
	);
};

const formatVendorAddress = (address) => {
	if (!address) return '-';
	if (typeof address === 'string') return address;
	if (typeof address === 'object') {
		return [address.zone, address.city, address.state, address.pincode]
			.filter(Boolean)
			.join(', ');
	}
	return '-';
};

function InvoiceDetailsContent({ vendorBillRes, regularTripData, adhocTripData, noLoadTripData }) {
	const dispatch = useDispatch();
	const uiVersion = getUIVersion();
	const useV2API = uiVersion === 'new';
	const vendorBill = vendorBillRes?.vendor_bill || {};
	const vendorBillId = vendorBill?.id || vendorBill?.vendor_bill_id;
	const isAdvancePaid = vendorBill?.is_advance_paid ?? true;
	const hasAdvancePayment = 1 || Boolean(vendorBill?.advance_utr && isAdvancePaid);
	const [markPaymentVisible, setMarkPaymentVisible] = useState(false);
	const [markPaymentLoading, setMarkPaymentLoading] = useState(false);
	const [pdfViewRecord, setPdfViewRecord] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const [existingPayments, setExistingPayments] = useState([]);
	const [loadingPayments, setLoadingPayments] = useState(false);

	// Fetch existing payments when modal opens (for modal validation)
	useEffect(() => {
		const fetchExistingPayments = async () => {
			if (vendorBillId && markPaymentVisible) {
				setLoadingPayments(true);
				try {
					const action = searchInvoicePayments({
						vendor_bill_id: vendorBillId,
						useV2API,
					});
					const response = await dispatch(action);
					const payments = response?.data || response?.rows || [];
					setExistingPayments(Array.isArray(payments) ? payments : []);
				} catch (error) {
					console.error('Error fetching existing payments:', error);
					setExistingPayments([]);
				} finally {
					setLoadingPayments(false);
				}
			}
		};

		fetchExistingPayments();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendorBillId, markPaymentVisible, useV2API]);

	const handleMarkPaymentSubmit = async (values) => {
		setMarkPaymentLoading(true);
		try {
			// values already contains all the data formatted by the modal (vendor_bill_id, paid_amount, utr_number, paid_date, status, document_key)
			const action = createInvoicePayment({
				req_data: values,
				useV2API,
			});

			const response = await dispatch(action);

			if (response?.status === 'success') {
				message.success('Payment created successfully');
				setMarkPaymentVisible(false);
				setRefreshKey((prev) => prev + 1);
				// Refresh payment history for summary and modal
				const refreshAction = searchInvoicePayments({
					vendor_bill_id: vendorBillId,
					useV2API,
				});
				const refreshResponse = await dispatch(refreshAction);
				const refreshedPayments = refreshResponse?.data || refreshResponse?.rows || [];
				setExistingPayments(Array.isArray(refreshedPayments) ? refreshedPayments : []);
				// Reload page to refresh all data
				window.location.reload();
			} else {
				const errorMsg =
					response?.error?.message || response?.message || 'Failed to create payment';
				message.error(errorMsg);
			}
		} catch (err) {
			console.error('Payment creation error:', err);
			message.error(err?.message || 'Failed to create payment');
		} finally {
			setMarkPaymentLoading(false);
		}
	};

	const handleChargesSave = () => {
		setRefreshKey((prev) => prev + 1);
		// Optionally reload the page or refresh data
		window.location.reload();
	};

	const handleDownloadPDF = async () => {
		if (!vendorBillId) {
			message.error('Vendor bill ID is missing');
			return;
		}

		try {
			const apiData = {
				report_type: 'vendor_bill',
				file_type: 'pdf',
				data: {
					id: vendorBillId,
					url_type: 'download',
				},
			};

			const CREDENTIALS = {
				url: '/generate-report',
				data: apiData,
				aclBlockApiCall: false,
				...(useV2API && { version: 'v2' }),
			};

			const response = await API.common(CREDENTIALS);

			if (response?.data?.download_url) {
				window.open(response.data.download_url, '_blank');
				message.success('PDF download started');
			} else {
				message.error('Failed to get download URL');
			}
		} catch (error) {
			console.error('Error downloading PDF:', error);
			message.error(error?.message || 'Failed to download PDF');
		}
	};

	const markPaymentRecord = {
		id: vendorBill?.id,
		request_value: vendorBill?.total_payable_amount,
	};

	return (
		<Container>
			<TitleRow>
				<TitleText style={{ marginBottom: 0 }}>Invoice</TitleText>
				<div style={{ display: 'flex', gap: '8px' }}>
					{vendorBillId && (
						<Button
							type="default"
							icon={<DownloadOutlined />}
							onClick={handleDownloadPDF}
						>
							Download PDF
						</Button>
					)}
					{vendorBill?.invoice_status === 'Pending' &&
						vendorBill?.has_pending_changes === false && (
							<MarkPaymentButton onClick={() => setMarkPaymentVisible(true)}>
								Mark Payment
							</MarkPaymentButton>
						)}
				</div>
			</TitleRow>
			<PaymentDetailsModal
				visible={markPaymentVisible}
				onClose={() => setMarkPaymentVisible(false)}
				record={markPaymentRecord}
				onSubmit={handleMarkPaymentSubmit}
				loading={markPaymentLoading || loadingPayments}
				vendorInvoice
				vendorBill={vendorBill}
				existingPayments={existingPayments}
				useV2API={useV2API}
			/>

			{/* Invoice Summary Card - Moved to top to match design */}
			<InvoiceSummaryCard
				vendorBillRes={vendorBillRes}
				vendorDetails={vendorBillRes?.vendor_details}
			/>

			<Card>
				<Flex>
					<FlexChild>
						<Flex mb={3}>
							<Tag>Bill No:</Tag>
							<Text>{vendorBillRes?.vendor_bill?.vendor_bill_no}</Text>
						</Flex>
						<Flex mb={3}>
							<Tag>Bill Date :</Tag>
							<Text>
								{moment
									.utc(vendorBillRes?.vendor_bill?.created_date)
									.local()
									.format('DD-MM-YYYY h:mm A')}
							</Text>
						</Flex>
					</FlexChild>
					<FlexChild>
						<Flex mb={3}>
							<Tag>Pay To:</Tag>
							<Text>
								{vendorBillRes?.vendor_details?.vendor_name},{' '}
								{formatVendorAddress(vendorBillRes?.vendor_details?.address)}
							</Text>
						</Flex>
						<Flex mb={3}>
							<Tag>Invoice To:</Tag>
							<Text>
								Intelligent Retail Pvt Ltd <br />
								G Floor, 413/C,
								<br />
								15th Cross Rd, Muneswara Nagar, Sector 6,
								<br />
								HSR Layout, Bengaluru, Karnataka-560102, India
								<br />
								GSTIN Number: 29AAFCI1726B1ZW
							</Text>
						</Flex>
					</FlexChild>
				</Flex>
				<SubTitle>Vendor Details</SubTitle>
				<Flex>
					<FlexChild>
						<Flex mb={3}>
							<Tag>PAN:</Tag>
							<Text>{vendorBillRes?.vendor_details?.pan_number}</Text>
						</Flex>
						<Flex mb={3}>
							<Tag>Bank:</Tag>
							<Text>{vendorBillRes?.vendor_details?.bank_name}</Text>
						</Flex>
						<Flex mb={3}>
							<Tag>Account No:</Tag>
							<Text>{vendorBillRes?.vendor_details?.acc_number}</Text>
						</Flex>
					</FlexChild>
					<FlexChild>
						<Flex mb={3}>
							<Tag>GSTIN:</Tag>
							<Text>{vendorBillRes?.vendor_details?.gst_number}</Text>
						</Flex>
						<Flex mb={3}>
							<Tag>Bank Branch:</Tag>
							<Text>{vendorBillRes?.vendor_details?.bank_branch}</Text>
						</Flex>
						<Flex mb={3}>
							<Tag>IFSC Code:</Tag>
							<Text>{vendorBillRes?.vendor_details?.ifsc_code}</Text>
						</Flex>
					</FlexChild>
				</Flex>
				<SubTitle>Invoice Details</SubTitle>
				<Flex>
					<FlexChild>
						<Flex mb={3}>
							<Tag>Project Location:</Tag>
							<Text>{vendorBillRes?.project_location_codes?.join(',  ')}</Text>
						</Flex>
						<Flex mb={3}>
							<Tag>No.Of Trips.:</Tag>
							<Text>{vendorBillRes?.vendor_bill?.no_of_trips}</Text>
						</Flex>
					</FlexChild>
					<FlexChild>
						<Flex mb={3}>
							<Tag>Bill Period.:</Tag>
							<Text>{vendorBillRes?.vendor_bill?.bill_period}</Text>
						</Flex>
						<Flex mb={3}>
							<Tag>Invoice Payment Status:</Tag>
							<Text>{vendorBillRes?.vendor_bill?.invoice_status}</Text>
						</Flex>
					</FlexChild>
				</Flex>
			</Card>
			{hasAdvancePayment && (
				<Card>
					<Flex>
						<FlexChild>
							<SubTitle>Advance Details</SubTitle>
							<Table
								dataSource={getAdvancePaymentData(adhocTripData)}
								columns={getAdvancePaymentColumns((rec) => setPdfViewRecord(rec))}
								pagination={false}
								summary={() =>
									getAdvancePaymentSummary(getAdvancePaymentData(adhocTripData))
								}
							/>
						</FlexChild>
					</Flex>
				</Card>
			)}
			<ProformaPdfViewerModal
				visible={Boolean(pdfViewRecord)}
				onClose={() => setPdfViewRecord(null)}
				viewUrl={pdfViewRecord?.proforma_view_url}
				invoiceNumber={pdfViewRecord?.proforma_invoice_number}
				pdfFilename={pdfViewRecord?.proforma_pdf_url}
			/>

			<Card>
				<SubTitle>Regular Trip Details</SubTitle>
				<Box mt={3}>
					{/* <RCTable
					rowKey="id"
					tableDatas={[rowData, setRowData]}
					// togglePaginate={[paginateConfig, setpaginateConfig]}
					dispatchAction={getListAction}
					columns={RegularTripTableConfig}
				/> */}
					<Table dataSource={regularTripData} columns={columnsRegularTrip} />
				</Box>
			</Card>
			<Card>
				<Flex>
					<FlexChild>
						<SubTitle>Adhoc Trip Details</SubTitle>

						{/* <RCTable
						rowKey="id"
						tableDatas={[rowData, setRowData]}
						// togglePaginate={[paginateConfig, setpaginateConfig]}
						dispatchAction={getListAction}
						columns={AdhocTripTableConfig}
					/> */}
						<Table dataSource={adhocTripData} columns={columnsAdhocTrip} />
					</FlexChild>
				</Flex>
			</Card>
			<Card>
				<SubTitle>No Load Trip Details</SubTitle>
				<Box mt={3}>
					{/* <RCTable
					rowKey="id"
					tableDatas={[rowData, setRowData]}
					// togglePaginate={[paginateConfig, setpaginateConfig]}
					dispatchAction={getListAction}
					columns={NoLoadTripTableConfig}
				/> */}
					<Table dataSource={noLoadTripData} columns={columnsNoLoadTrip} />
				</Box>
			</Card>
			{/* Editable Total Payable - Shows original table structure with edit functionality */}
			<EditableTotalPayable
				vendorBillRes={vendorBillRes}
				onSave={handleChargesSave}
				onCancel={() => {}}
				isEditable
				useV2API={useV2API}
				vendorBillId={vendorBillId}
				key={refreshKey}
			/>

			{/* Edit History Table */}
			{vendorBillId && (
				<EditHistoryTable
					vendorBillId={vendorBillId}
					useV2API={useV2API}
					key={`edit-history-${refreshKey}`}
				/>
			)}

			{/* Payment History Table */}
			{vendorBillId && (
				<PaymentHistoryTable
					vendorBillId={vendorBillId}
					useV2API={useV2API}
					vendorBillRes={vendorBillRes}
					key={`payment-history-${refreshKey}`}
				/>
			)}
		</Container>
	);
}

InvoiceDetailsContent.propTypes = {
	vendorBillRes: PropTypes.object,
	regularTripData: PropTypes.array,
	adhocTripData: PropTypes.array,
	noLoadTripData: PropTypes.array,
};

export default InvoiceDetailsContent;
